/* jshint esversion: 11 */
import './styles/main.scss';
import 'katex/dist/katex.min.css';
import React, {useEffect, lazy} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';
import moment from 'moment';
import {connect} from 'react-redux';
import { ThemeProvider } from '@studygo/frontend-components';
import { Keyboard } from '@capacitor/keyboard';
import * as sharedActions from './pagesNext/shared/Actions';
import { isAuthenticated } from './services/AccessToken';
import {
	BRAND_NAME,
	DEFAULT_LANGUAGE_LOCALE,
	DEVICE_TYPE,
	GTM_CONTAINER_ID,
	VWO_ACCOUNT_ID,
} from './services/Constants';
import {
	IS_IOS_APP,
	IS_MOBILE_APP,
} from './services/capacitor/Constants';
import {
	getLocalStorageItem,
	getSessionStorageItem,
	isNlLocale,
	isTabletLandscape,
	setLocalStorageItem,
	setSessionStorageItem,
} from './services/Helper';
import useVWOScript from './services/hocs/useVWOScript';
import useTagManagerScript from './services/hocs/useTagManagerScript';
import useThemeSwitch from './services/hocs/useThemeSwitch';
import usePushNotifications from './components/appPushNotification/components/PushNotification';

import WebSocketWorker from './components/WebSocketWorker';
import { updateStatusBar } from './services/capacitor/Helper';

const Router = lazy(() => import('./Router'));
const ErrorBoundary = lazy(() => import('./components/errorBoundary/components/ErrorBoundary'));
const AddImageModal = lazy(() => import('./components/modals/addImage/components/AddImage'));
const AddListsModal = lazy(() => import('./components/modals/addListModal/components/AddListsModal'));
const ConfirmationModal = lazy(() => import('./components/modals/confirmation/ConfirmationModal'));
const CopyListModal = lazy(() => import('./components/modals/copyList/CopyListModal'));
const CreateFolder = lazy(() => import('./components/modals/createFolder/components/CreateFolder'));
const QuestionModal = lazy(() => import('./components/modals/createQuestion/components/QuestionModal'));
const ImagePreviewModal = lazy(() => import('./components/modals/imagePreview/components/ImagePreviewModal'));
const OnboardingModal = lazy(() => import('./components/modals/onboarding/components/OnboardingModal'));
const ParentRequestModal = lazy(() => import('./components/modals/parentRequest/components/ParentRequestModal'));
const PayInvoiceModal = lazy(() => import('./components/modals/payInvoice/components/PayInvoice'));
const ReportModal = lazy(() => import('./components/modals/reportModal/components/ReportModal'));
const TutorChatModal = lazy(() => import('./components/modals/chat/components/TutorChatLanding'));
const SwitchPackageModal = lazy(() => import('./components/modals/switchPackage/components/SwitchPackageModal'));
const ManageQuizModal = lazy(() => import('./components/modals/manageQuizModal/components/ManageQuizModal'));
const Notification = lazy(() => import('./components/notification/components/Notification'));
const Notifications = lazy(() => import('./components/notifications/components/Notifications'));
const ImageOverviewModal = lazy(() => import('./pages/createList/modals/ImageOverview'));
const ImportModal = lazy(() => import('./pages/createList/modals/Import'));
const CreateGroupModal = lazy(() => import('./pages/groupOverview/modals/CreateGroup'));
const AddWYSIWYGImage = lazy(() => import('./components/wysiwyg/addImage/components/AddWYSIWYGImageModal'));
const AddWYSIWYGVideo = lazy(() => import('./components/wysiwyg/addVideo/components/AddWYSIWYGVideoModal'));
const AddWYSIWYGAnswer = lazy(() => import('./components/wysiwyg/addAnswer/components/AddWYSIWYGAnswerModal'));
const AddWYSIWYGFormula = lazy(() => import('./components/wysiwyg/addFormula/components/AddWYSIWYGFormulaModal'));
const AddWYSIWYGAudio = lazy(() => import('./components/wysiwyg/addAudio/components/AddWYSIWYGAudioModal'));
const ChangeEmailModal = lazy(() => import('./pages/profileSettings/components/modals/ChangeEmailModal'));
const ReactivateSubscriptionModal = lazy(() => import('./pages/packages/components/ReactivateSubscriptionModal'));
const AchievementsModal = lazy(() => import('./components/modals/achievements/components/AchievementsModal'));
const StudySongModal = lazy(() => import('./components/modals/studySong/components/StudySongModal'));
const LessonRequestModal = lazy(() => import('./components/modals/lessonRequest/components/LessonRequestModal'));
const NPSModal = lazy(() => import('./components/modals/nps/components/NPSModal'));
const StreaksModal = lazy(() => import('./components/modals/streaks/components/StreaksModal'));
const ReverseTrialModal = lazy(() => import('./components/modals/reverseTrial/components/ReverseTrialModal'));
const ShareModal = lazy(() => import('./components/modals/share/components/ShareModal'));

const App = ({ theme, selectedThemeOption, setTheme, userData }) => {
	const { t } = useTranslation();

	usePushNotifications();
	useVWOScript(VWO_ACCOUNT_ID);
	useTagManagerScript(GTM_CONTAINER_ID);

	const isTouch =
		'ontouchstart' in document.documentElement ||
		'ontouchstart' in window ||
		navigator.msMaxTouchPoints > 0;
	const isIOS = /iPod|iPhone|iPad|MacIntel/.test(navigator.platform) && isTouch;

	DEVICE_TYPE.touch = isTouch;
	DEVICE_TYPE.type = isIOS ? 'ios' : 'other';

	const html = document.getElementsByTagName('html')[0];
	let initialWindowHeight = window.innerHeight;
	let initialScreenHeight = window.screen.height;

	const checkIsKeyboard = () => {
		if(IS_MOBILE_APP && userData?.theme?.selected_theme === 'light')  updateStatusBar(isTabletLandscape() ? 'dark': 'light') ;
		if (IS_IOS_APP || isIOS) return;

		if (initialScreenHeight !== window.screen.height) {
			initialScreenHeight = window.screen.height;
			initialWindowHeight = window.innerHeight;
		}

		const isKeyboard = window.innerHeight < initialWindowHeight;
		const isBottomTabBarActive = IS_MOBILE_APP ? false : window.innerHeight < window.screen.availHeight;

		if (isKeyboard && !isBottomTabBarActive) {
			window.dispatchEvent(new Event('keyboardDidShow'));
		} else {
			window.dispatchEvent(new Event('keyboardDidHide'));
		}
	};

	useThemeSwitch(selectedThemeOption, setTheme, userData);

	useEffect(() => {
		if (isTouch) {
			html.classList.add('touch-device');
			if(isIOS) html.classList.add('is-iOS');

			window.addEventListener('resize', checkIsKeyboard, false);

			return () => {
				window.removeEventListener('resize', checkIsKeyboard);
			};
		}
		return undefined;
	}, [checkIsKeyboard, isIOS, isTouch, html.classList]);


	useEffect(() => {
		const handleScroll = () => {
			if(document.documentElement?.classList.contains('keyboard-on') && IS_MOBILE_APP) {
				setTimeout(() => {
					Keyboard?.hide();
				}, 100);
			}

			if (document.activeElement instanceof HTMLInputElement) {
				document.activeElement.blur();
			}
		};

		document.addEventListener('touchmove', handleScroll);

		return () => {
			document.removeEventListener('touchmove', handleScroll);
		};
	}, [IS_MOBILE_APP]);

	useEffect(() => {
		const uuid = uuidv4();
		const sessionId = getSessionStorageItem('sessionId');
		const deviceId = getLocalStorageItem('deviceId');

		if (!sessionId) {
			setSessionStorageItem('sessionId', uuid);
		}
		if (!deviceId) {
			setLocalStorageItem('deviceId', uuid);
		}
		if (isNlLocale()) moment.locale(DEFAULT_LANGUAGE_LOCALE);

		document.addEventListener('keydown', handleKeydown, false);

		return () => {
			document.removeEventListener('keydown', handleKeydown);
		};
	}, []);

	const handleKeydown = (e) => {
		const { activeElement} = document || {};
		if (e.key === 'Enter' &&
			DEVICE_TYPE.touch &&
			!activeElement.classList.contains("ql-editor") &&
			activeElement.tagName !== "TEXTAREA") {
			setTimeout(() => {
				activeElement.blur();
			}, 100);
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Helmet>
				<title>{BRAND_NAME}</title>
				<meta name="description" content={t('meta_descriptions.main', { brandName: BRAND_NAME })} />
			</Helmet>
			<ErrorBoundary>
				{isAuthenticated() && <WebSocketWorker />}
				<Notification />
				<ConfirmationModal />
				<CreateFolder />
				<CreateGroupModal />
				<ParentRequestModal />
				<OnboardingModal />
				<AddListsModal />
				<AddImageModal />
				<ImageOverviewModal />
				<ImportModal />
				<Notifications />
				<ImagePreviewModal />
				<QuestionModal />
				<ReportModal />
				<SwitchPackageModal />
				<CopyListModal />
				<PayInvoiceModal />
				<ManageQuizModal />
				<AddWYSIWYGImage />
				<AddWYSIWYGVideo />
				<AddWYSIWYGAnswer />
				<AddWYSIWYGFormula />
				<AddWYSIWYGAudio />
				<ChangeEmailModal />
				<ReactivateSubscriptionModal />
				<AchievementsModal />
				<StudySongModal />
				<LessonRequestModal />
				<NPSModal />
				<StreaksModal />
				<ReverseTrialModal />
				<ShareModal />
				<Router />
				<TutorChatModal />
			</ErrorBoundary>
		</ThemeProvider>
	);
};

const mapStateToProps = ({sharedReducer, sidebarReducer}) => ({
	theme: sharedReducer.theme,
	selectedThemeOption: sharedReducer.selectedThemeOption,
	userData: sidebarReducer.userData,
});

const mapDispatchToProps = {
	setTheme: sharedActions.setTheme,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
